// assets/patch-preview.js
/**
 * Theme glue for the patch configurator.
 *
 * This is the only file in the patch system that knows about Vero, the product
 * form or the media gallery. Everything it orchestrates lives in the framework
 * free `@patch/*` modules, so moving the configurator into a theme app
 * extension means re-pointing the import map and re-rendering the markup, not
 * rewriting the engine.
 *
 * Responsibilities:
 *
 * - read merchant configuration out of the block
 * - own the single customisation state object
 * - drive the renderer that draws the patch over the product photo
 * - accept artwork from an upload or from the existing design picker
 * - keep line item properties and generated snapshot files in sync
 *
 * There is deliberately no second preview beside the controls. The product
 * photo is the preview, so the customer sees the patch in context and drags it
 * where it will actually sit.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import {
  buildInitialState,
  getAlignment,
  getEngravingColor,
  getEngravingOptions,
  getMaterial,
  getMaterialSetId,
  getPatchMaterials,
  mergeConfigSources,
  normalizeConfig,
  parseConfigJson,
} from '@patch/config';
import { PatchState, changed, createDefaultState } from '@patch/state';
import { PatchRenderer } from '@patch/renderer';
import { collectTextureUrls, textureCache } from '@patch/materials';
import { CONTOUR_SHAPE_ID, PRESET_SHAPE_IDS, getShape, getShapes } from '@patch/shapes';
import {
  DEFAULT_ARTWORK_POSITION,
  clampPosition,
  loadArtworkFromFile,
  loadArtworkSource,
  releaseArtworkSource,
} from '@patch/artwork';
import {
  PreviewMount,
  applyAlignment,
  applyAlignmentAdjustment,
  describeMedia,
  listMedia,
} from '@patch/alignment';
import { generateDieCutShape } from '@patch/diecut';
import { attachFileToInput, exportPatchPng, exportPatchSvg, forgetDataUrl } from '@patch/export';

/** How long to wait after the last change before regenerating snapshot files. */
const SNAPSHOT_DEBOUNCE = 700;

/** Query parameter that switches on the merchant alignment editor. */
const ALIGNMENT_EDITOR_PARAM = 'patch-align';

/**
 * Runs a callback when the browser is idle, falling back to a timeout.
 *
 * @param {() => void} callback - Work to run.
 * @param {number} timeout - Maximum delay.
 * @returns {number}
 */
function whenIdle(callback, timeout) {
  const idle = /** @type {typeof window.requestIdleCallback | undefined} */ (
    /** @type {any} */ (window).requestIdleCallback
  );

  if (idle) return idle(callback, { timeout });

  return window.setTimeout(callback, timeout);
}

/**
 * The element positioned over the product image. It is created in JavaScript
 * and moved into the gallery, so it never interferes with server-rendered
 * markup or the gallery's own morphing.
 */
export class PatchPreviewOverlay extends HTMLElement {}

if (!customElements.get('patch-preview-overlay')) {
  customElements.define('patch-preview-overlay', PatchPreviewOverlay);
}

/**
 * @typedef {object} PatchDesignerRefs
 * @property {HTMLScriptElement} [configData] - JSON configuration payload.
 * @property {HTMLElement} [errors] - Error region.
 * @property {HTMLElement} [errorHeading] - Focusable error heading.
 * @property {HTMLElement} [errorMessage] - Error text.
 * @property {HTMLInputElement} [artworkInput] - Customer file input.
 * @property {HTMLInputElement} [artworkSubmissionInput] - Validated artwork file posted with the product form.
 * @property {HTMLElement} [artworkFileName] - Selected file name output.
 * @property {HTMLButtonElement} [artworkChoose] - Themed button that opens the file picker.
 * @property {HTMLElement} [artworkStatus] - Polite live region for Contour upload prompts.
 * @property {HTMLElement} [artworkControls] - Positioning controls wrapper.
 * @property {HTMLInputElement} [propShape] - Hidden shape property.
 * @property {HTMLInputElement} [propPrimary] - Hidden patch material property.
 * @property {HTMLInputElement} [propColor] - Hidden patch color property, used when materials are grouped.
 * @property {HTMLInputElement} [propMode] - Hidden artwork method property.
 * @property {HTMLInputElement} [propEngraving] - Hidden engraving color property.
 * @property {HTMLInputElement} [propDieCut] - Hidden die-cut property.
 * @property {HTMLInputElement} [propDesign] - Hidden selected design property.
 * @property {HTMLInputElement} [propPayload] - Hidden JSON payload property.
 * @property {HTMLInputElement} [mockupInput] - Generated PNG file input.
 * @property {HTMLInputElement} [vectorInput] - Generated SVG file input.
 * @property {HTMLElement[]} [groups] - Control groups that can be hidden.
 * @property {HTMLScriptElement} [materialsData] - Material library payload.
 * @property {HTMLScriptElement} [advancedData] - Merchant JSON overrides.
 * @property {HTMLElement} [shapeOptions] - Container for shape swatches.
 * @property {HTMLSelectElement} [materialSetSelect] - Material set value, kept as a real form control.
 * @property {HTMLElement} [materialSetControl] - Wrapper around the material set dropdown.
 * @property {HTMLButtonElement} [materialSetButton] - Button that opens the material set menu.
 * @property {HTMLElement} [materialSetValue] - Shows the selected set on the button.
 * @property {HTMLElement} [materialSetMenu] - The material set listbox.
 * @property {HTMLElement} [materialLegend] - Legend above the material swatches.
 * @property {HTMLElement} [primaryOptions] - Container for primary material swatches.
 * @property {HTMLElement} [engravingOptions] - Container for engraving color swatches.
 *
 * @extends Component<PatchDesignerRefs>
 */
class PatchDesigner extends Component {
  /** @type {import('./patch-config.js').PatchConfig | null} */
  #config = null;

  /** @type {PatchState | null} */
  #store = null;

  /** @type {PatchRenderer[]} */
  #renderers = [];

  /** @type {PatchPreviewOverlay | null} */
  #overlay = null;

  /** @type {PreviewMount | null} */
  #mount = null;

  /** @type {import('./patch-artwork.js').ArtworkSource | null} */
  #artwork = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number | null} */
  #snapshotTimer = null;

  /** @type {number} */
  #snapshotToken = 0;

  /** @type {Map<string, import('./patch-shapes.js').PatchShape>} */
  #customShapes = new Map();

  /** @type {number} */
  #artworkToken = 0;

  /** @type {Map<number, PointerEvent>} */
  #pointers = new Map();

  /** @type {{ x: number, y: number, distance: number, scale: number } | null} */
  #dragOrigin = null;

  /** Degrees the overlay is tilted by on the current photo. */
  #overlayRotation = 0;

  /** @type {{ destroy: () => void, syncToMedia: () => void } | null} */
  #alignmentEditor = null;

  /** Unique suffix so radio groups never collide with another block on the page. */
  #uid = Math.random().toString(36).slice(2, 8);

  /** Identifies the engraving options on screen so they rebuild only when they change. */
  #engravingSignature = '';

  /** Set the material swatches on screen belong to. Null until they are first built. */
  #materialSetSignature = /** @type {string | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    this.#customShapes.clear();
    this.#materialSetSignature = null;
    this.#engravingSignature = '';

    const config = this.#readConfig();
    if (!config) return;

    this.#config = config;
    this.#store = new PatchState(buildInitialState(config, createDefaultState()));

    this.#populateControls();
    this.#syncControlsFromState(this.#store.state);
    this.#setupRenderers();
    this.#bindEvents();
    this.#syncFromDesignPicker({ silent: true });

    this.#store.subscribe((state, changes) => this.#handleStateChange(state, changes));
    this.#handleStateChange(this.#store.state, new Set(['*']));

    // Swatch thumbnails are already in the markup; warm the large textures so
    // the first swatch change is instant.
    whenIdle(() => textureCache.preload(collectTextureUrls(config.materials)), 2000);

    if (config.customShapes.length > 0) void this.#loadCustomShapes();

    if (new URLSearchParams(window.location.search).has(ALIGNMENT_EDITOR_PARAM)) {
      void this.#loadAlignmentEditor();
    }
  }

  /**
   * Traces the merchant's uploaded shapes and folds them into the library.
   *
   * The controls are built before this finishes so the configurator is usable
   * straight away; a merchant shape simply gains its outline a moment later. If
   * a shape is already selected when it arrives, the preview is redrawn.
   */
  async #loadCustomShapes() {
    const config = this.#config;
    if (!config) return;

    const { loadCustomShapes } = await import('@patch/svg-shape');
    const { shapes, warnings } = await loadCustomShapes(config.customShapes);

    if (!this.isConnected) {
      if (warnings.length > 0) console.warn('[patch-designer]', warnings.join(' '));
      return;
    }

    for (const shape of shapes) this.#customShapes.set(shape.id, shape);
    if (warnings.length > 0) console.warn('[patch-designer]', warnings.join(' '));

    const loadedIds = new Set(shapes.map((shape) => shape.id));
    const failedIds = new Set(
      config.customShapes
        .map((shape) => shape.id)
        .filter((id) => !loadedIds.has(id) && !getShape(id, this.#customShapes))
    );

    config.shapes = config.shapes.filter((id) => !failedIds.has(id));
    if (config.shapes.length === 0) config.shapes = [...PRESET_SHAPE_IDS];

    const fallbackShape = config.shapes.find((id) => getShape(id, this.#customShapes));
    if (fallbackShape && !getShape(config.defaultShape, this.#customShapes)) {
      config.defaultShape = fallbackShape;
    }

    const state = this.#store?.state;
    if (!state) return;

    if (!getShape(state.shape, this.#customShapes)) {
      this.#store?.update({ shape: config.defaultShape, isDieCut: false });
    }

    const currentState = this.#store?.state;
    if (!currentState) return;

    this.#populateControls();
    this.#syncControlsFromState(currentState);
    this.#handleStateChange(currentState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = null;

    if (this.#snapshotTimer !== null) clearTimeout(this.#snapshotTimer);
    this.#snapshotToken += 1;
    this.#artworkToken += 1;

    for (const renderer of this.#renderers) renderer.destroy();
    this.#renderers = [];

    this.#alignmentEditor?.destroy();
    this.#alignmentEditor = null;

    this.#mount?.stop();
    this.#mount = null;

    releaseArtworkSource(this.#artwork);
    this.#artwork = null;
  }

  /** @returns {import('./patch-state.js').PatchStateShape | null} */
  get state() {
    return this.#store?.state ?? null;
  }

  /**
   * Makes the current customization safe to serialize into the product form.
   *
   * @returns {Promise<boolean>}
   */
  async prepareForSubmit() {
    const config = this.#config;
    const store = this.#store;

    if (!config || !store) {
      this.#showError(
        this.dataset.errorConfig ||
          'This product is not set up for live preview yet. Please contact us and we will help with your order.'
      );
      return false;
    }

    if (store.state.shape === CONTOUR_SHAPE_ID && !this.#artwork) {
      this.#promptArtworkForContour();
      return false;
    }

    if (store.state.shape === CONTOUR_SHAPE_ID && !store.state.isDieCut) {
      this.#showError(
        this.dataset.errorContourTrace ||
          "We couldn't trace that artwork. Try a PNG with a clear subject and transparent background."
      );
      return false;
    }

    const ready = await this.#flushSnapshot();

    if (!ready) {
      this.#showError(
        this.dataset.errorSnapshot ||
          "We couldn't prepare the production files for this patch. Please try again before adding it to your cart."
      );
    }

    return ready;
  }

  // -- setup ----------------------------------------------------------------

  /**
   * @returns {import('./patch-config.js').PatchConfig | null}
   */
  #readConfig() {
    const materials = parseConfigJson(this.refs.materialsData?.textContent ?? null);
    const blockConfig = parseConfigJson(this.refs.configData?.textContent ?? null);
    const advancedConfig = parseConfigJson(this.refs.advancedData?.textContent ?? null);
    const metafieldConfig = parseConfigJson(this.dataset.metafieldConfig ?? null);

    // Later sources win: the metaobject library is the base, block settings
    // shape it, the merchant's JSON overrides that, and a product metafield has
    // the final say so one block can serve many products.
    const merged = mergeConfigSources(materials, blockConfig, advancedConfig, metafieldConfig);
    const { config, errors, warnings } = normalizeConfig(merged);

    for (const warning of warnings) console.warn(`[patch] ${warning}`);

    if (errors.length > 0) {
      console.error('[patch] configuration errors', errors);
      this.#showError(
        this.dataset.errorConfig ||
          'This product is not set up for live preview yet. Please contact us and we will help with your order.'
      );
      this.setAttribute('data-patch-unavailable', 'true');
      return null;
    }

    return config;
  }

  /**
   * Builds the option lists from the configuration.
   *
   * Shape geometry and the material library both live in the engine, so the
   * markup ships empty containers and they are filled here rather than being
   * duplicated in Liquid.
   */
  #populateControls() {
    const config = this.#config;
    if (!config) return;

    const shapeContainer = this.refs.shapeOptions;

    if (shapeContainer) {
      const shapes = getShapes(
        config.shapes.filter((id) => id !== CONTOUR_SHAPE_ID),
        this.#customShapes
      );

      // Contour is offered as a shape swatch when die cut is allowed. It is not
      // a geometric preset: selecting it traces the patch from the artwork.
      if (config.dieCut.allowed) {
        const contour = getShape(CONTOUR_SHAPE_ID);

        if (contour) {
          shapes.push({
            ...contour,
            name: this.dataset.contourLabel || contour.name,
          });
        }
      }

      shapeContainer.replaceChildren(
        ...shapes.map((shape) => {
          const preview = `<svg viewBox="0 0 ${shape.width} ${shape.height}" aria-hidden="true" focusable="false"><path d="${shape.outline}"/></svg>`;
          return this.#buildSwatch({
            control: 'shape',
            group: 'shape',
            value: shape.id,
            name: shape.name,
            visual: preview,
          });
        })
      );
    }

    // The swatches themselves belong to whichever set is selected, so they are
    // left to `#reconcileMaterials` and rebuilt whenever that changes.
    const setSelect = this.refs.materialSetSelect;

    if (setSelect) {
      setSelect.replaceChildren(
        ...config.materialSets.map((set) => {
          const option = document.createElement('option');
          option.value = set.id;
          option.textContent = set.name;
          return option;
        })
      );
    }

    this.refs.materialSetMenu?.replaceChildren(
      ...config.materialSets.map((set) => {
        const option = document.createElement('li');

        option.className = 'patch-designer__select-option';
        option.role = 'option';
        option.tabIndex = -1;
        option.dataset.value = set.id;
        option.textContent = set.name;

        return option;
      })
    );

    const legend = this.refs.materialLegend;

    if (legend && config.materialSets.length > 1 && legend.dataset.groupedLabel) {
      legend.textContent = legend.dataset.groupedLabel;
    }

    // A production method the merchant has switched off should not be offered.
    for (const [mode, allowed] of [
      ['engraved', config.artwork.allowEngraved],
      ['uv-print', config.artwork.allowUvPrint],
    ]) {
      const option = this.querySelector(`[data-patch-mode="${mode}"]`);
      if (option instanceof HTMLElement) option.hidden = !allowed;
    }
  }

  /**
   * Rebuilds the material swatches for the set currently selected.
   *
   * Which set that is comes from the selected material rather than from state
   * of its own, so the picker and the swatches cannot disagree about which set
   * is showing.
   *
   * @param {import('./patch-state.js').PatchStateShape} state
   */
  #reconcileMaterials(state) {
    const config = this.#config;
    const container = this.refs.primaryOptions;
    if (!config || !container) return;

    const setId = getMaterialSetId(config, state.materials.primary);
    if (setId === this.#materialSetSignature) return;

    this.#materialSetSignature = setId;

    container.replaceChildren(
      ...getPatchMaterials(config, setId || null).map((material) =>
        this.#buildSwatch({
          control: 'primary',
          group: 'primary',
          value: material.id,
          name: material.name,
          // The swatch shows the material itself, so customers judge grain and
          // sheen rather than a flat approximation of the color.
          style: `--swatch-color: ${material.fallbackColor};${
            material.swatchImage ? ` --swatch-image: url("${material.swatchImage}");` : ''
          }`,
        })
      )
    );

    // Freshly built radios start unchecked, so reflect the stored material.
    for (const input of container.querySelectorAll('[data-patch-control="primary"]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === state.materials.primary;
    }

    this.#syncMaterialSetControl(setId);
  }

  /**
   * Points the dropdown at a set.
   *
   * The button carries the current value as its own text, so it has to be told
   * what that value is whenever the set changes underneath it — including when
   * the change came from somewhere other than the dropdown, such as a saved
   * configuration being restored.
   *
   * @param {string} setId - Set currently in effect.
   */
  #syncMaterialSetControl(setId) {
    const select = this.refs.materialSetSelect;
    if (select && select.value !== setId) select.value = setId;

    for (const option of this.refs.materialSetMenu?.children ?? []) {
      if (option instanceof HTMLElement) option.ariaSelected = String(option.dataset.value === setId);
    }

    const value = this.refs.materialSetValue;
    const name = this.#config?.materialSets.find((set) => set.id === setId)?.name ?? '';

    if (value && value.textContent !== name) value.textContent = name;
  }

  /**
   * Opens or closes the material set menu.
   *
   * @param {boolean} open - Whether the menu should be showing.
   */
  #toggleMaterialSetMenu(open) {
    const menu = this.refs.materialSetMenu;
    const button = this.refs.materialSetButton;
    if (!menu || !button) return;

    menu.hidden = !open;
    button.ariaExpanded = String(open);

    if (!open) return;

    // Opening moves focus into the list so the arrow keys work straight away,
    // starting from the current value rather than the top of the list.
    const selected = menu.querySelector('[aria-selected="true"]') ?? menu.firstElementChild;
    if (selected instanceof HTMLElement) selected.focus();
  }

  /**
   * Keyboard handling for the material set menu.
   *
   * @param {KeyboardEvent} event
   */
  #handleMaterialSetKeydown(event) {
    const menu = this.refs.materialSetMenu;
    const button = this.refs.materialSetButton;
    if (!menu || !button) return;

    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.refs.materialSetControl?.contains(target)) return;

    if (target === button) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      event.preventDefault();
      this.#toggleMaterialSetMenu(true);
      return;
    }

    const options = /** @type {HTMLElement[]} */ ([...menu.children]);
    const index = options.indexOf(target);

    switch (event.key) {
      case 'Escape':
        this.#toggleMaterialSetMenu(false);
        button.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        options[Math.min(index + 1, options.length - 1)]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        options[Math.max(index - 1, 0)]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        options[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        options[options.length - 1]?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#chooseMaterialSet(target);
        break;
      default:
        break;
    }
  }

  /**
   * Applies the set an option represents and closes the menu.
   *
   * @param {HTMLElement} option - The chosen `role="option"` element.
   */
  #chooseMaterialSet(option) {
    const setId = option.dataset.value;
    if (!setId) return;

    this.#selectMaterialSet(setId);
    this.#syncMaterialSetControl(setId);
    this.#toggleMaterialSetMenu(false);
    this.refs.materialSetButton?.focus();
  }

  /**
   * Moves the selection to the first material in a set.
   *
   * @param {string} setId - Set the customer picked.
   */
  #selectMaterialSet(setId) {
    const config = this.#config;
    const store = this.#store;
    if (!config || !store) return;

    if (getMaterialSetId(config, store.state.materials.primary) === setId) return;

    const [first] = getPatchMaterials(config, setId);
    if (first) store.update({ materials: { primary: first.id } });
  }

  /**
   * Rebuilds the engraving swatches for the material currently selected, and
   * corrects the stored color when the previous choice is impossible on it.
   *
   * @param {import('./patch-state.js').PatchStateShape} state
   * @returns {boolean} True when the color was corrected and a fresh state pass is already running.
   */
  #reconcileEngraving(state) {
    const config = this.#config;
    const store = this.#store;
    if (!config || !store) return false;

    const { colors, fixed, material } = getEngravingOptions(config, state.materials.primary);
    const signature = `${material?.id ?? ''}|${fixed}|${colors.map((color) => color.id).join(',')}`;

    if (signature !== this.#engravingSignature) {
      this.#engravingSignature = signature;

      const container = this.refs.engravingOptions;

      if (container) {
        container.replaceChildren(
          ...colors.map((color) =>
            this.#buildSwatch({
              control: 'engraving',
              group: 'engraving',
              value: color.value,
              name: color.name,
              style: `--swatch-color: ${color.value};`,
            })
          )
        );

        // With only one possible result there is nothing to choose, so the
        // swatch list gives way to a plain statement of what will happen.
        container.hidden = fixed;
      }

      const note = this.refs.engravingNote;

      if (note instanceof HTMLElement) {
        note.hidden = !fixed;
        note.textContent = fixed
          ? (note.dataset.template ?? 'Engraves to [color].')
              .replace('[color]', colors[0]?.name ?? '')
              .replace('[material]', material?.name ?? '')
          : '';
        note.style.setProperty('--patch-engraving-color', colors[0]?.value ?? '#141414');
      }

      // Freshly built radios start unchecked, so reflect the stored color.
      for (const input of this.querySelectorAll('[data-patch-control="engraving"]')) {
        if (input instanceof HTMLInputElement) input.checked = input.value === state.artwork.engravingColor;
      }
    }

    const allowed = colors.some((color) => color.value === state.artwork.engravingColor);
    const [first] = colors;

    if (!allowed && first) {
      store.update({ artwork: { engravingColor: first.value } });
      return true;
    }

    return false;
  }

  /**
   * @param {object} options - Swatch options.
   * @param {string} options.control - Value for `data-patch-control`.
   * @param {string} options.group - Radio group suffix.
   * @param {string} options.value - Input value.
   * @param {string} options.name - Visible label.
   * @param {string} [options.style] - Inline custom properties for the visual.
   * @param {string} [options.visual] - Inline markup rendered inside the visual.
   * @returns {HTMLElement}
   */
  #buildSwatch({ control, group, value, name, style = '', visual = '' }) {
    const label = document.createElement('label');
    label.className = 'patch-designer__swatch';

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = `patch-${group}-${this.#uid}`;
    input.value = value;
    input.dataset.patchControl = control;

    const swatch = document.createElement('span');
    swatch.className = 'patch-designer__swatch-visual';
    if (style) swatch.setAttribute('style', style);
    if (visual) swatch.innerHTML = visual;

    const caption = document.createElement('span');
    caption.className = 'patch-designer__swatch-name';
    caption.textContent = name;

    label.append(input, swatch, caption);

    return label;
  }

  /**
   * Reflects the initial state into the controls so the markup and the store
   * agree before the customer touches anything.
   *
   * @param {import('./patch-state.js').PatchStateShape} state
   */
  #syncControlsFromState(state) {
    /** @type {Record<string, string>} */
    const values = {
      // Contour stays selected while die cut is active, even though the live
      // silhouette is the traced artwork rather than the Contour placeholder.
      shape: state.isDieCut || state.shape === CONTOUR_SHAPE_ID ? CONTOUR_SHAPE_ID : state.shape,
      primary: state.materials.primary ?? '',
      engraving: state.artwork.engravingColor ?? '',
      mode: state.artwork.mode,
    };

    for (const [control, value] of Object.entries(values)) {
      for (const input of this.querySelectorAll(`[data-patch-control="${control}"]`)) {
        if (!(input instanceof HTMLInputElement)) continue;
        input.checked = input.value === value;
      }
    }

    this.#syncPositionInputs(state.artwork.position);
    this.#syncPreviewAdjustmentInputs(state.preview.adjustment);
  }

  #setupRenderers() {
    const config = this.#config;
    if (!config) return;

    const overlay = /** @type {PatchPreviewOverlay} */ (document.createElement('patch-preview-overlay'));
    overlay.className = 'patch-preview-overlay';
    overlay.setAttribute('aria-hidden', 'true');

    const renderer = new PatchRenderer({ config, customShapes: this.#customShapes });
    renderer.mount(overlay);

    this.#renderers.push(renderer);
    this.#overlay = overlay;

    const galleryRoot = this.#findGalleryRoot();

    // Without a gallery there is nowhere to show the patch, but the renderer
    // still runs off-document so the cart snapshots are produced as usual.
    if (!galleryRoot) {
      console.warn('[patch] no product gallery found, the preview overlay is hidden');
      return;
    }

    this.#mount = new PreviewMount({
      element: overlay,
      root: galleryRoot,
      getMediaId: () => this.#store?.state.preview.mediaId ?? null,
      onAttach: (target) => this.#handleOverlayAttached(target),
    });

    this.#mount.start();
  }

  /**
   * @returns {Element | null}
   */
  #findGalleryRoot() {
    const selector = this.dataset.gallerySelector || 'media-gallery';
    const section = this.closest('.shopify-section') ?? document;

    return section.querySelector(selector) ?? document.querySelector(selector);
  }

  /**
   * @param {Element | null} target
   */
  #handleOverlayAttached(target) {
    if (!target || !this.#overlay || !this.#config) return;

    const baseAlignment = getAlignment(this.#config, describeMedia(target, this.#findGalleryRoot()));
    const adjustment = this.#store?.state.preview.adjustment ?? { x: 0, y: 0, size: 0 };
    const alignment = applyAlignmentAdjustment(baseAlignment, adjustment);

    // Kept so dragging can undo the tilt: the customer moves the artwork along
    // the patch's own axes, not the screen's.
    this.#overlayRotation = alignment.rotation;

    // While the merchant is aligning, the editor owns the overlay. Applying the
    // saved alignment here would throw away the adjustment they just made every
    // time a variant swapped the photo.
    if (this.#alignmentEditor) {
      this.#alignmentEditor.syncToMedia();
      return;
    }

    // A photo the patch does not belong on, typically a back or detail shot.
    // Hidden rather than unmounted so swiping back to a front view restores it.
    this.#overlay.hidden = alignment.hide;

    applyAlignment(this.#overlay, alignment);
  }

  #bindEvents() {
    const signal = this.#controller?.signal;
    if (!signal) return;

    this.addEventListener('change', (event) => this.#handleControlEvent(event), { signal });
    this.addEventListener('input', (event) => this.#handleControlEvent(event), { signal });
    this.addEventListener('click', (event) => this.#handleClick(event), { signal });
    this.addEventListener('keydown', (event) => this.#handleMaterialSetKeydown(event), { signal });

    // Clicking anywhere else closes the menu, which is the behaviour every
    // other dropdown on the page has.
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        const inside = target instanceof Node && this.refs.materialSetControl?.contains(target);

        if (!inside) this.#toggleMaterialSetMenu(false);
      },
      { signal }
    );

    document.addEventListener('hatlabDesignSelected', () => this.#syncFromDesignPicker({}), { signal });
    document.addEventListener('hatlabDesignCleared', () => this.#handleDesignCleared(), { signal });

    // A variant change can swap the gallery element itself, which would leave
    // the mount observing a detached node.
    const section = this.closest('.shopify-section');
    section?.addEventListener(
      ThemeEvents.variantUpdate,
      () => {
        requestAnimationFrame(() => this.#mount?.setRoot(this.#findGalleryRoot()));
      },
      { signal }
    );

    if (this.#overlay) this.#bindDragging(this.#overlay, signal);
  }

  // -- control handling -----------------------------------------------------

  /**
   * @param {Event} event
   */
  #handleControlEvent(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const control = target.dataset.patchControl;
    if (!control || !this.#store) return;

    if (target instanceof HTMLInputElement && target.type === 'file') {
      void this.#handleFileSelected(target);
      return;
    }

    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;

    const { value } = target;

    switch (control) {
      case 'shape':
        void this.#selectShape(value);
        break;
      case 'primary':
        this.#store.update({ materials: { primary: value } });
        break;
      case 'material-set':
        this.#selectMaterialSet(value);
        break;
      case 'mode':
        this.#store.update({ artwork: { mode: value === 'uv-print' ? 'uv-print' : 'engraved' } });
        break;
      case 'engraving':
        this.#store.update({ artwork: { engravingColor: value } });
        break;
      case 'text':
        this.#store.update({ text: { value: value.slice(0, this.#config?.text.maxLength ?? 24) } });
        break;
      case 'position-x':
      case 'position-y':
      case 'position-scale':
      case 'position-rotation':
        this.#updatePosition(control, Number.parseFloat(value));
        break;
      case 'preview-x':
      case 'preview-y':
      case 'preview-size':
        this.#updatePreviewAdjustment(control, Number.parseFloat(value));
        break;
      default:
        break;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (this.refs.materialSetButton?.contains(target)) {
      this.#toggleMaterialSetMenu(this.refs.materialSetMenu?.hidden ?? false);
      return;
    }

    const option = target.closest('.patch-designer__select-option');

    if (option instanceof HTMLElement && this.refs.materialSetMenu?.contains(option)) {
      this.#chooseMaterialSet(option);
      return;
    }

    const action = target.closest('[data-patch-action]')?.getAttribute('data-patch-action');
    if (!action) return;

    switch (action) {
      case 'reset-artwork':
        event.preventDefault();
        this.#store?.update({ artwork: { position: { ...DEFAULT_ARTWORK_POSITION } } });
        break;
      case 'reset-preview':
        event.preventDefault();
        this.#store?.update({ preview: { adjustment: { x: 0, y: 0, size: 0 } } });
        this.#syncPreviewAdjustmentInputs({ x: 0, y: 0, size: 0 });
        break;
      case 'remove-artwork':
        event.preventDefault();
        this.#clearArtwork();
        break;
      case 'upload':
        event.preventDefault();
        this.refs.artworkInput?.click();
        break;
      default:
        break;
    }
  }

  /**
   * @param {string} control
   * @param {number} value
   */
  #updatePosition(control, value) {
    const store = this.#store;
    if (!store || !Number.isFinite(value)) return;

    const current = store.state.artwork.position;
    /** @type {Partial<import('./patch-state.js').PatchPosition>} */
    const next = {};

    if (control === 'position-x') next.x = value;
    if (control === 'position-y') next.y = value;
    if (control === 'position-scale') next.scale = value;
    if (control === 'position-rotation') next.rotation = value;

    store.update({ artwork: { position: clampPosition(next, current) } });
  }

  /**
   * @param {string} control
   * @param {number} value
   */
  #updatePreviewAdjustment(control, value) {
    const store = this.#store;
    if (!store || !Number.isFinite(value)) return;

    /** @type {Partial<import('./patch-state.js').PatchPreviewAdjustment>} */
    const next = {};

    if (control === 'preview-x') next.x = value;
    if (control === 'preview-y') next.y = value;
    if (control === 'preview-size') next.size = value;

    store.update({ preview: { adjustment: next } });
  }

  // -- artwork --------------------------------------------------------------

  /**
   * @param {HTMLInputElement} input
   */
  async #handleFileSelected(input) {
    const config = this.#config;
    const store = this.#store;
    const file = input.files?.[0];

    if (!config || !store || !file) return;

    this.#clearError();

    const token = (this.#artworkToken += 1);
    const result = await loadArtworkFromFile(file, config.artwork, this.#artworkMessages());

    if (token !== this.#artworkToken) {
      if ('source' in result) releaseArtworkSource(result.source);
      return;
    }

    if ('error' in result) {
      this.#showError(result.error);
      input.value = '';
      this.#restoreArtworkFileName();
      return;
    }

    if (
      !this.refs.artworkSubmissionInput ||
      !attachFileToInput(this.refs.artworkSubmissionInput, file)
    ) {
      releaseArtworkSource(result.source);
      input.value = '';
      this.#restoreArtworkFileName();
      this.#showError(
        this.dataset.errorFileAttach ||
          "We couldn't attach that artwork to your order. Please choose the file again."
      );
      return;
    }

    this.#setArtwork(result.source);

    if (this.refs.artworkFileName) this.refs.artworkFileName.textContent = result.source.fileName;

    store.update({
      artwork: {
        source: 'upload',
        fileName: result.source.fileName,
        designId: null,
        designTitle: null,
        naturalWidth: result.source.width,
        naturalHeight: result.source.height,
        isVector: result.source.isVector,
        position: { ...DEFAULT_ARTWORK_POSITION },
      },
    });

    this.#clearArtworkPrompt();

    if (store.state.isDieCut || store.state.shape === CONTOUR_SHAPE_ID) {
      await this.#enableContour();
    }
  }

  /** Restores the placeholder the theme rendered, which is the translated one. */
  #resetFileName() {
    const output = this.refs.artworkFileName;
    if (!output) return;

    output.textContent = output.dataset.empty ?? '';
  }

  #restoreArtworkFileName() {
    const output = this.refs.artworkFileName;
    const state = this.#store?.state;
    if (!output || !state) return;

    output.textContent = state.artwork.fileName ?? state.artwork.designTitle ?? output.dataset.empty ?? '';
  }

  /** @returns {import('./patch-artwork.js').PatchArtworkMessages} */
  #artworkMessages() {
    return {
      fileEmpty: this.dataset.errorFileEmpty,
      fileType: this.dataset.errorFileType,
      fileSize: this.dataset.errorFileSize,
      fileRead: this.dataset.errorFileRead,
      fileDimensions: this.dataset.errorFileDimensions,
    };
  }

  /**
   * @param {import('./patch-artwork.js').ArtworkSource | null} source
   */
  #setArtwork(source) {
    if (this.#artwork) {
      forgetDataUrl(this.#artwork.url);
      releaseArtworkSource(this.#artwork);
    }

    this.#artwork = source;

    for (const renderer of this.#renderers) renderer.setArtwork(source);
  }

  #clearArtwork() {
    const store = this.#store;
    if (!store) return;

    this.#artworkToken += 1;
    this.#setArtwork(null);

    if (this.refs.artworkInput) this.refs.artworkInput.value = '';
    if (this.refs.artworkSubmissionInput) attachFileToInput(this.refs.artworkSubmissionInput, null);
    this.#resetFileName();

    for (const renderer of this.#renderers) renderer.setDieCutShape(null);

    store.update({
      isDieCut: false,
      artwork: {
        source: 'none',
        fileName: null,
        designId: null,
        designTitle: null,
        naturalWidth: 0,
        naturalHeight: 0,
        isVector: false,
        position: { ...DEFAULT_ARTWORK_POSITION },
      },
    });

    this.#clearArtworkPrompt();
  }

  // -- design picker --------------------------------------------------------

  /**
   * Adopts whatever the existing design gallery has selected. The design
   * picker owns that state; this only reads it and reacts to its events.
   *
   * @param {object} options - Sync options.
   * @param {boolean} [options.silent] - Skip when no design is selected.
   */
  async #syncFromDesignPicker({ silent = false }) {
    const config = this.#config;
    const store = this.#store;
    if (!config || !store || !config.artwork.allowDesignPicker) return;

    const design = window.HATLAB_DESIGN?.getSelectedDesign?.();

    if (!design) {
      if (!silent) this.#handleDesignCleared();
      return;
    }

    const url = design.previewImage || design.displayImage || design.thumbnail;
    if (!url) return;
    if (store.state.artwork.designId === design.id && this.#artwork) return;

    const token = (this.#artworkToken += 1);
    const result = await loadArtworkSource({
      url,
      config: config.artwork,
      messages: this.#artworkMessages(),
    });

    if (token !== this.#artworkToken) {
      if ('source' in result) releaseArtworkSource(result.source);
      return;
    }

    if ('error' in result) {
      this.#showError(
        this.dataset.errorDesign || "We couldn't load that design for preview. Please try another design."
      );
      return;
    }

    this.#setArtwork(result.source);

    if (this.refs.artworkInput) this.refs.artworkInput.value = '';
    if (this.refs.artworkSubmissionInput) attachFileToInput(this.refs.artworkSubmissionInput, null);
    if (this.refs.artworkFileName) this.refs.artworkFileName.textContent = design.title ?? '';

    store.update({
      artwork: {
        source: 'design',
        designId: design.id ?? null,
        designTitle: design.title ?? null,
        fileName: null,
        naturalWidth: result.source.width,
        naturalHeight: result.source.height,
        isVector: result.source.isVector,
        position: { ...DEFAULT_ARTWORK_POSITION },
      },
    });

    this.#clearArtworkPrompt();

    if (store.state.isDieCut || store.state.shape === CONTOUR_SHAPE_ID) {
      await this.#enableContour();
    }
  }

  #handleDesignCleared() {
    const store = this.#store;
    if (!store || store.state.artwork.source !== 'design') return;

    this.#clearArtwork();
  }

  // -- contour (artwork-shaped cut) -----------------------------------------

  /**
   * Selects a geometric shape or the Contour option.
   *
   * @param {string} value - Shape id, or `contour`.
   */
  async #selectShape(value) {
    const store = this.#store;
    if (!store) return;

    this.#clearError();

    if (value === CONTOUR_SHAPE_ID) {
      await this.#selectContour();
      return;
    }

    this.#clearArtworkPrompt();

    for (const renderer of this.#renderers) renderer.setDieCutShape(null);
    store.update({ shape: value, isDieCut: false });
  }

  /**
   * Activates Contour. Without artwork the upload control is highlighted
   * instead of showing an error; the cut is built once a file arrives.
   */
  async #selectContour() {
    const store = this.#store;
    if (!store) return;

    store.update({ shape: CONTOUR_SHAPE_ID, isDieCut: false });

    for (const renderer of this.#renderers) renderer.setDieCutShape(null);

    if (!this.#artwork) {
      this.#promptArtworkForContour();
      return;
    }

    await this.#enableContour();
  }

  /** Traces the artwork silhouette and switches the preview to that cut. */
  async #enableContour() {
    const store = this.#store;
    if (!store || !this.#artwork) return;

    store.update({ shape: CONTOUR_SHAPE_ID, isDieCut: true });
    await this.#rebuildDieCut();
  }

  async #rebuildDieCut() {
    const config = this.#config;
    const store = this.#store;
    if (!config || !store || !this.#artwork) return;

    const result = generateDieCutShape(this.#artwork, config.dieCut, {
      read: this.dataset.errorContourRead,
      empty: this.dataset.errorContourEmpty,
      trace: this.dataset.errorContourTrace,
    });

    if ('error' in result) {
      this.#showError(result.error);
      store.update({ shape: CONTOUR_SHAPE_ID, isDieCut: false });

      for (const renderer of this.#renderers) renderer.setDieCutShape(null);
      return;
    }

    for (const renderer of this.#renderers) renderer.setDieCutShape(result.shape);
    store.update({
      shape: CONTOUR_SHAPE_ID,
      isDieCut: true,
      artwork: { position: { ...DEFAULT_ARTWORK_POSITION } },
    });
  }

  /**
   * Guides the customer to upload artwork when Contour is chosen first.
   * Focus moves to the choose button so keyboard and screen reader users land
   * on the next step without an error banner.
   */
  #promptArtworkForContour() {
    const group = this.#artworkGroup();
    const choose = this.refs.artworkChoose;
    const status = this.refs.artworkStatus;
    const message =
      this.dataset.contourPrompt || 'Add your artwork to cut the patch to its shape.';

    this.#clearError();

    if (!group || group.hidden) {
      this.#showError(message);
      return;
    }

    if (status) {
      status.textContent = message;
      status.hidden = false;
    }

    group?.classList.add('is-highlighted');

    requestAnimationFrame(() => {
      choose?.focus();
      group?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });

    window.setTimeout(() => {
      group?.classList.remove('is-highlighted');
    }, 2400);
  }

  #clearArtworkPrompt() {
    const status = this.refs.artworkStatus;

    this.#artworkGroup()?.classList.remove('is-highlighted');

    if (status) {
      status.textContent = '';
      status.hidden = true;
    }
  }

  /** @returns {HTMLElement | null} */
  #artworkGroup() {
    const group = this.querySelector('[data-patch-group="artwork"]');
    return group instanceof HTMLElement ? group : null;
  }

  // -- rendering ------------------------------------------------------------

  /**
   * @param {import('./patch-state.js').PatchStateShape} state
   * @param {Set<string>} changes
   */
  #handleStateChange(state, changes) {
    this.#reconcileMaterials(state);

    // A corrected color re-enters this method with the fixed state, so let that
    // pass do the work rather than rendering and saving a value we just replaced.
    if (this.#reconcileEngraving(state)) return;

    for (const renderer of this.#renderers) renderer.render(state);

    if (changed(changes, 'preview.adjustment')) {
      this.#handleOverlayAttached(this.#mount?.target ?? null);
    }

    this.#updateControlVisibility(state);
    this.#syncProperties(state);
    this.#scheduleSnapshot();
  }

  /**
   * Hides options that do not apply to the current selection, which is what
   * keeps one interface usable across very different products.
   *
   * @param {import('./patch-state.js').PatchStateShape} state
   */
  #updateControlVisibility(state) {
    const config = this.#config;
    if (!config) return;

    const hasArtwork = state.artwork.source !== 'none';
    const hideUploadWithDesign =
      state.artwork.source === 'design' && this.#artworkGroup()?.dataset.hideWhenDesign === 'true';

    /** @type {Record<string, boolean>} */
    const visibility = {
      shape: true,
      'material-set': config.materialSets.length > 1,
      mode: config.artwork.allowEngraved && config.artwork.allowUvPrint,
      engraving: state.artwork.mode === 'engraved',
      artwork: config.artwork.allowUpload && !hideUploadWithDesign,
      position: hasArtwork,
      'preview-position': hasArtwork,
      text: config.text.enabled,
    };

    for (const group of this.refs.groups ?? []) {
      const name = group.dataset.patchGroup;
      if (!name || !(name in visibility)) continue;

      const visible = visibility[name] ?? true;

      group.hidden = !visible;
      group.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    this.#overlay?.classList.toggle('patch-preview-overlay--empty', !hasArtwork);

    // The overlay only intercepts pointers once there is artwork to move,
    // otherwise it would block swiping through the gallery for no reason.
    this.#overlay?.classList.toggle('patch-preview-overlay--draggable', hasArtwork);
  }

  // -- cart data ------------------------------------------------------------

  /**
   * @param {import('./patch-state.js').PatchStateShape} state
   */
  #syncProperties(state) {
    const config = this.#config;
    if (!config) return;

    const primary = getMaterial(config, state.materials.primary);
    const engraving = config.engravingColors.find((color) => color.value === state.artwork.engravingColor);
    const shape = this.#renderers[0]?.shape;

    // Grouped, the set is the material and the swatch is the color. Ungrouped,
    // the swatch is the material and there is no color to report.
    const grouped = config.materialSets.length > 1;

    const isContour = state.isDieCut || state.shape === CONTOUR_SHAPE_ID;
    const contourLabel = this.dataset.contourLabel || 'Contour';

    setInputValue(this.refs.propShape, isContour ? contourLabel : (shape?.name ?? state.shape));
    setInputValue(this.refs.propPrimary, (grouped ? primary?.setName : primary?.name) ?? '');
    setInputValue(this.refs.propColor, grouped ? (primary?.name ?? '') : '');
    setInputValue(this.refs.propDieCut, state.isDieCut ? 'Yes' : '');
    setInputValue(this.refs.propDesign, state.artwork.designTitle ?? '');
    setInputValue(
      this.refs.propMode,
      state.artwork.source === 'none' ? '' : state.artwork.mode === 'uv-print' ? 'Full color UV print' : 'Engraved'
    );
    setInputValue(
      this.refs.propEngraving,
      state.artwork.mode === 'engraved' && state.artwork.source !== 'none' ? (engraving?.name ?? '') : ''
    );

    // Everything a fulfilment team needs, kept out of the customer's way. A
    // leading underscore hides the property in the cart and at checkout while
    // preserving it on the order.
    setInputValue(
      this.refs.propPayload,
      JSON.stringify({
        v: config.version,
        shape: state.isDieCut ? 'die-cut' : state.shape,
        dieCut: state.isDieCut,
        materials: { ...state.materials, set: primary?.set || null },
        artwork: {
          source: state.artwork.source,
          mode: state.artwork.mode,
          engravingColor: state.artwork.engravingColor,
          fileName: state.artwork.fileName,
          designId: state.artwork.designId,
          position: state.artwork.position,
        },
        text: config.text.enabled ? state.text.value : undefined,
        preview: state.preview,
      })
    );
  }

  #scheduleSnapshot() {
    if (!this.refs.mockupInput && !this.refs.vectorInput) return;
    if (this.#snapshotTimer !== null) clearTimeout(this.#snapshotTimer);

    const token = (this.#snapshotToken += 1);

    this.#snapshotTimer = window.setTimeout(() => {
      this.#snapshotTimer = null;
      whenIdle(() => void this.#generateSnapshot(token), 2000);
    }, SNAPSHOT_DEBOUNCE);
  }

  /** @returns {Promise<boolean>} */
  async #flushSnapshot() {
    if (this.#snapshotTimer !== null) {
      clearTimeout(this.#snapshotTimer);
      this.#snapshotTimer = null;
    }

    const token = (this.#snapshotToken += 1);
    return this.#generateSnapshot(token);
  }

  /**
   * @param {number} token
   * @returns {Promise<boolean>}
   */
  async #generateSnapshot(token) {
    const svg = this.#renderers[0]?.svg;
    const state = this.#store?.state;
    if (!svg || !state) return false;

    // Nothing worth attaching until the customer has supplied artwork.
    if (state.artwork.source === 'none') {
      if (this.refs.mockupInput) attachFileToInput(this.refs.mockupInput, null);
      if (this.refs.vectorInput) attachFileToInput(this.refs.vectorInput, null);
      return true;
    }

    let complete = true;

    if (this.refs.mockupInput) {
      const png = await exportPatchPng(svg, { fileName: 'patch-mockup.png' });
      if (token !== this.#snapshotToken) return false;

      const attached = Boolean(png && !png.partial && attachFileToInput(this.refs.mockupInput, png.file));
      if (!attached) {
        attachFileToInput(this.refs.mockupInput, null);
        complete = false;
      }
    }

    if (this.refs.vectorInput) {
      const vector = await exportPatchSvg(svg, { fileName: 'patch-artwork.svg' });
      if (token !== this.#snapshotToken) return false;

      const attached = Boolean(
        vector && !vector.partial && attachFileToInput(this.refs.vectorInput, vector.file)
      );
      if (!attached) {
        attachFileToInput(this.refs.vectorInput, null);
        complete = false;
      }
    }

    return complete;
  }

  // -- dragging -------------------------------------------------------------

  /**
   * Lets the customer drag and pinch the artwork directly on the product photo.
   *
   * Pointer events cover mouse, touch and pen with one code path. The range
   * inputs in the markup provide the keyboard-accessible equivalent, so nothing
   * here is the only route to a setting.
   *
   * @param {HTMLElement} overlay
   * @param {AbortSignal} signal
   */
  #bindDragging(overlay, signal) {
    overlay.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.#store || this.#store.state.artwork.source === 'none') return;

        overlay.setPointerCapture(event.pointerId);
        this.#pointers.set(event.pointerId, event);

        if (this.#pointers.size === 1) {
          this.#dragOrigin = {
            x: event.clientX,
            y: event.clientY,
            distance: 0,
            scale: this.#store.state.artwork.position.scale,
          };
        }

        overlay.classList.add('is-dragging');
      },
      { signal }
    );

    overlay.addEventListener(
      'pointermove',
      (event) => {
        if (!this.#pointers.has(event.pointerId)) return;

        // Stops the gallery treating the drag as a swipe to the next photo.
        event.preventDefault();
        event.stopPropagation();
        this.#pointers.set(event.pointerId, event);

        if (this.#pointers.size >= 2) {
          this.#handlePinch();
          return;
        }

        this.#handleDrag(event, overlay);
      },
      { signal }
    );

    const endPointer = (/** @type {PointerEvent} */ event) => {
      this.#pointers.delete(event.pointerId);

      if (this.#pointers.size === 0) {
        this.#dragOrigin = null;
        overlay.classList.remove('is-dragging');
      }
    };

    overlay.addEventListener('pointerup', endPointer, { signal });
    overlay.addEventListener('pointercancel', endPointer, { signal });
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLElement} overlay
   */
  #handleDrag(event, overlay) {
    const store = this.#store;
    const origin = this.#dragOrigin;
    const shape = this.#renderers[0]?.shape;
    if (!store || !origin || !shape) return;

    const rect = overlay.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    let movedX = event.clientX - origin.x;
    let movedY = event.clientY - origin.y;

    // The overlay is usually tilted to match the photo. Rotating the movement
    // back means dragging right walks the artwork along the patch, not the
    // screen, which is what the customer sees happening.
    if (this.#overlayRotation !== 0) {
      const radians = (-this.#overlayRotation * Math.PI) / 180;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);

      [movedX, movedY] = [movedX * cos - movedY * sin, movedX * sin + movedY * cos];
    }

    // `getBoundingClientRect` grows once the overlay is tilted, so measure with
    // `offsetWidth`, which stays the untilted width the alignment asked for.
    const width = overlay.offsetWidth || rect.width;

    // Translate pixels into viewBox units, then into a percentage of the safe
    // area, which is the unit the artwork position is stored in.
    const unitsPerPixel = shape.width / width;
    const deltaX = (movedX * unitsPerPixel * 100) / shape.safeArea.width;
    const deltaY = (movedY * unitsPerPixel * 100) / shape.safeArea.height;

    const current = store.state.artwork.position;

    this.#dragOrigin = { ...origin, x: event.clientX, y: event.clientY };

    store.update({
      artwork: { position: clampPosition({ x: current.x + deltaX, y: current.y + deltaY }, current) },
    });

    this.#syncPositionInputs(store.state.artwork.position);
  }

  #handlePinch() {
    const store = this.#store;
    const origin = this.#dragOrigin;
    if (!store || !origin) return;

    const [first, second] = [...this.#pointers.values()];
    if (!first || !second) return;

    const distance = Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);

    if (origin.distance === 0) {
      this.#dragOrigin = { ...origin, distance, scale: store.state.artwork.position.scale };
      return;
    }

    const current = store.state.artwork.position;
    const scale = origin.scale * (distance / origin.distance);

    store.update({ artwork: { position: clampPosition({ scale }, current) } });
    this.#syncPositionInputs(store.state.artwork.position);
  }

  /**
   * Keeps the numeric controls in step with direct manipulation.
   *
   * @param {import('./patch-state.js').PatchPosition} position
   */
  #syncPositionInputs(position) {
    /** @type {Record<string, number>} */
    const values = {
      'position-x': position.x,
      'position-y': position.y,
      'position-scale': position.scale,
      'position-rotation': position.rotation,
    };

    for (const [control, value] of Object.entries(values)) {
      const input = this.querySelector(`[data-patch-control="${control}"]`);
      if (input instanceof HTMLInputElement) input.value = String(Math.round(value * 100) / 100);
    }
  }

  /**
   * @param {import('./patch-state.js').PatchPreviewAdjustment} adjustment
   */
  #syncPreviewAdjustmentInputs(adjustment) {
    /** @type {Record<string, number>} */
    const values = {
      'preview-x': adjustment.x,
      'preview-y': adjustment.y,
      'preview-size': adjustment.size,
    };

    for (const [control, value] of Object.entries(values)) {
      const input = this.querySelector(`[data-patch-control="${control}"]`);
      if (input instanceof HTMLInputElement) input.value = String(value);
    }
  }

  // -- alignment editor -----------------------------------------------------

  async #loadAlignmentEditor() {
    if (!this.#overlay || !this.#config || !this.#store) return;

    try {
      const { mountAlignmentEditor } = await import('@patch/alignment-editor');

      this.#alignmentEditor = mountAlignmentEditor({
        overlay: this.#overlay,
        config: this.#config,
        getMedia: () => (this.#mount?.target ? describeMedia(this.#mount.target, this.#findGalleryRoot()) : null),
        listMedia: () => listMedia(this.#findGalleryRoot()),
      });
    } catch (error) {
      console.warn('[patch] alignment editor unavailable', error);
    }
  }

  // -- messaging ------------------------------------------------------------

  /**
   * @param {string} message
   */
  #showError(message) {
    const region = this.refs.errors;
    if (!region) return;

    if (this.refs.errorMessage) this.refs.errorMessage.textContent = message;

    region.hidden = false;

    // Move focus to the start of the message so screen reader users hear it
    // from the beginning rather than mid-sentence.
    requestAnimationFrame(() => {
      this.refs.errorHeading?.focus();
    });
  }

  #clearError() {
    const region = this.refs.errors;
    if (!region) return;

    region.hidden = true;
    if (this.refs.errorMessage) this.refs.errorMessage.textContent = '';
  }
}

/**
 * @param {HTMLInputElement | undefined} input
 * @param {string} value
 */
function setInputValue(input, value) {
  if (!input) return;

  // Empty properties are disabled so Shopify does not show blank rows in the cart.
  input.disabled = value === '';
  if (input.value !== value) input.value = value;
}

if (!customElements.get('patch-designer')) {
  customElements.define('patch-designer', PatchDesigner);
}

export { PatchDesigner };
